import { motion } from "framer-motion";
import { Lightbulb, Briefcase, Users, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Lightbulb,
    title: "Everyday Tips",
    desc: "Family safety, password sanity, and spotting social-media scams before they spot you. Free, bite-sized, and actually understandable.",
    tag: "For you & your family",
    bg: "bg-secondary",
    img: "https://images.unsplash.com/photo-1758687126499-9ff30d1c5762?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjB1c2luZyUyMGxhcHRvcHxlbnwwfHx8fDE3ODgyMjg1Nzl8MA&ixlib=rb-4.1.0&q=85",
    imgAlt: "Parent and child learning together on a laptop",
  },
  {
    icon: Briefcase,
    title: "Small Biz Consult",
    desc: "Practical security setups, team training, and safe habits for Calgary small businesses — protection that doesn't break the bank.",
    tag: "For local business",
    bg: "bg-card",
    img: "https://images.pexels.com/photos/3933017/pexels-photo-3933017.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    imgAlt: "Calgary small business owner smiling at her shop entrance",
  },
  {
    icon: Users,
    title: "Community Hub",
    desc: "Local YYC meetups, workshops, and a knowledge-sharing crew where no question is too basic. Because Calgary looks out for its own.",
    tag: "For neighbours",
    bg: "bg-accent",
    img: null,
  },
];

export default function Services() {
  return (
    <section id="services" className="relative overflow-hidden py-24 lg:py-32 bg-muted/50 scroll-mt-20" data-testid="services-section">
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <span className="absolute -right-20 -top-24 font-display font-semibold text-[22rem] leading-none text-[#A9CDE9]/20 rotate-[12deg]">c</span>
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-16"
        >
          <p className="font-hand text-2xl text-primary mb-3">what we do</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="services-heading">
            Security help that sounds like a neighbour,{" "}
            <span className="text-primary">not a textbook.</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="services-grid">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`${s.bg} border border-border rounded-3xl overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow] duration-300`}
              data-testid={`service-card-${i}`}
            >
              {s.img && (
                <div className="aspect-video overflow-hidden">
                  <img src={s.img} alt={s.imgAlt} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-8 lg:p-10 flex flex-col flex-1">
                <s.icon className="w-8 h-8 text-primary mb-6" strokeWidth={1.8} />
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{s.tag}</p>
                <h3 className="font-display font-semibold text-2xl tracking-tight mb-4">{s.title}</h3>
                <p className="text-muted-foreground text-sm md:text-base leading-relaxed flex-1">{s.desc}</p>
                <a
                  href="#contact"
                  data-testid={`service-cta-${i}`}
                  className="mt-8 inline-flex items-center gap-1.5 font-display font-medium text-sm text-primary group"
                >
                  Let's talk
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" strokeWidth={2.2} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
